import { store } from "src/store/store";
import {
  RPCRequests,
  RPCResponses,
  Message
} from "src/modules/communication-protobuf";
import environment from "src/utils/environment";
import * as actions from "src/modules/activity-log/duck/actions";
import * as selectors from "src/modules/activity-log/duck/selectors";
import createActivityEmitter from "src/modules/audio-processing/createActivityEmitter";
import {
  getDeviceName,
  getUserId,
  isFreemium
} from "src/modules/auth/duck/selectors";
import { CACHE_NAME } from "src/modules/activity-log/constants";
import { getSignedUrl } from "src/modules/activity-log/ajax/cloud";
import { getActivityLogUploader } from "src/modules/activity-log/lib/ActivityLogUploader";
import BrowserUploader from "src/browser/BrowserUploader";

import createPushNotificator from "./createPushNotificator";
import MediaRecorder from "./MediaRecorder";
import EventManager from "./EventManager";

import { v4 as uuid4 } from "uuid";
const log = window.require("electron-log");
export default class ActivityLogManager {
  constructor({
    messenger,
    monitoring,
    getParents,
    updateParent,
    rpc,
    rtcManager,
    noiseMeter
  }) {
    this.rtcManager = rtcManager;
    this.messenger = messenger;
    this.getState = store.getState;
    this.dispatch = store.dispatch;
    this.rpc = rpc;
    this.getParents = getParents;
    this.updateParent = updateParent;
    this.noiseMeter = noiseMeter;
    this.monitoring = {
      ...monitoring,
      deviceName: getDeviceName(this.getState()),
      events: []
    };
    this.nextUpdateNumber = 1;
    this.nextEventId = 2;
    this.inProgressEvent = {
      start: 0,
      eventType: "SLEEPING",
      duration: 0,
      id: 1
    };

    this.isFreeVersion = () => isFreemium(this.getState());
    this.getMonitoringId = () =>
      this.monitoring ? this.monitoring.monitoringId : null;
    this.getIfIsValidRequest = (jid) =>
      this.monitoring != null && this.getParents()[jid];

    this.activityLogUploader = environment.isBrowser
      ? new BrowserUploader()
      : getActivityLogUploader();
    this.mediaRecorder = new MediaRecorder({
      rpc: this.rpc,
      getParents: this.getParents,
      rtcManager: this.rtcManager,
      activityLogUploader: this.activityLogUploader,
      getMonitoring: () => this.monitoring,
      claimNextEventId: this.claimNextEventId,
      getFileName: (suffix) => this.getFileName(suffix),
      notifyParents: this.notifyParents
    });
    this.eventManager = new EventManager({ mediaRecorder: this.mediaRecorder });
    this.activityEmitter = createActivityEmitter({
      noiseMeter: this.noiseMeter,
      durationToSleep: 5000,
      awakeMinimalVolume: 2 / 7
    });
    this.pushNotificator = createPushNotificator({
      monitoringId: this.monitoring.monitoringId,
      messenger,
      getActivityLogActivity: this.activityEmitter.getActivity,
      eventManager: this.eventManager
    });
    this.activityEmitter.on("activity-change", this.handleActivityChange);
    this.rpc.on(
      RPCRequests.StartActivityLog.name,
      this.handleStartActivityLogRpcRequest
    );
    this.rpc.on(
      RPCRequests.RefreshActivityLog.name,
      this.handleRefreshActivityLogRpcRequest
    );
    this.rpc.on(
      RPCRequests.StopActivityLog.name,
      this.handleStopActivityLogRpcRequest
    );
    this.rpc.on(
      RPCRequests.GetSignedUrl.name,
      this.handleGetSignedUrlRpcRequest
    );
    this.dispatch(
      actions.monitoringStarted({
        ...this.monitoring,
        events: [],
        startDate: new Date(this.monitoring.startDate)
      })
    );
    this.mediaRecorder.initialize();
  }

  claimNextEventId = () => {
    const id = this.nextEventId;
    this.nextEventId++;
    return id;
  };

  getFileName = (suffix) => {
    const userId = getUserId(this.getState());
    const storageId = selectors.getStorageId(this.getState());
    return `${userId}_${
      this.monitoring.monitoringId
    }_${suffix}_${uuid4()}.${storageId}`;
  };

  createFinishedEvent = () => {
    return {
      ...this.inProgressEvent,
      duration:
        Date.now() - (+this.monitoring.startDate + this.inProgressEvent.start),
      fileRelatedLength: 0
    };
  };

  handleActivityChange = async (activity) => {
    const finishedEvent = this.createFinishedEvent();
    this.monitoring.events.push(finishedEvent);

    const id = this.claimNextEventId();
    this.inProgressEvent = {
      start: finishedEvent.start + finishedEvent.duration,
      eventType: activity,
      duration: 0,
      id
    };

    if (activity === "AWAKE") {
      this.eventManager.onNoise(id);
      if (!this.isFreeVersion()) {
        this.inProgressEvent.fileName = this.getFileName("al-audio");
        this.activityLogUploader.uploadAudioEvent({
          eventType: this.inProgressEvent.eventType,
          fileName: this.inProgressEvent.fileName,
          monitoringId: this.monitoring.monitoringId,
          onSample: this.pushNotificator.getIfShouldRequestAudioSample()
            ? this.pushNotificator.onSample
            : null
        });
      }
    }
    if (finishedEvent.eventType === "AWAKE" && !this.isFreeVersion()) {
      await this.activityLogUploader.finishAudioUpload({
        fileName: finishedEvent.fileName
      });
    }

    this.dispatch(
      actions.eventEnded({
        monitoringId: this.monitoring.monitoringId,
        event: finishedEvent
      })
    );

    const message = Message.ActivityLogUpdateMessage.create({
      updateNumber: finishedEvent.id,
      eventToUpdate: finishedEvent,
      eventToCreate: { ...this.inProgressEvent, id: this.nextEventId }
    });

    this.notifyParents({ parents: this.getParents(), message });
  };

  notifyParents = ({ parents, message }) => {
    const parentsToNotify = Object.values(parents).filter(
      (parent) =>
        parent.requestedActivityLog &&
        parent.monitoringModeType === "FOREGROUND"
    );

    if (parentsToNotify.length === 0) return;
    parentsToNotify.forEach(({ jid }) => {
      this.messenger.sendAsEnvelope({ to: jid, payload: { message } });
    });
  };

  createActivityLogRpcResponse = () => {
    const { monitoringId, startDate, avatarId, deviceName, events } =
      this.monitoring;
    const nextUpdateNumber = this.nextUpdateNumber;
    this.nextUpdateNumber++;
    return RPCResponses.ActivityLog.create({
      inProgressEvent: {
        ...this.inProgressEvent,
        duration: Date.now() - (+startDate + this.inProgressEvent.start)
      },
      session: {
        monitoringId,
        startDate: startDate.toISOString(),
        duration: Date.now() - +startDate,
        deviceName,
        avatarId,
        events
      },
      nextUpdateNumber
    });
  };

  handleStartActivityLogRpcRequest = (request, from, sendResponse) => {
    if (!this.getIfIsValidRequest(from)) return;

    const response = this.createActivityLogRpcResponse();

    sendResponse(response);
    this.updateParent(from, { requestedActivityLog: true });
  };
  handleRefreshActivityLogRpcRequest = (request, from, sendResponse) => {
    if (!this.getIfIsValidRequest(from)) return;
    const response = this.createActivityLogRpcResponse();
    sendResponse(response);
  };

  handleStopActivityLogRpcRequest = (request, from, sendResponse) => {
    if (!this.getIfIsValidRequest(from)) return;
    this.nextUpdateNumber = 1;
    this.updateParent(from, { requestedActivityLog: false });
    sendResponse();
  };

  handleGetSignedUrlRpcRequest = async (request, from, sendResponse) => {
    if (!this.getIfIsValidRequest(from)) return;

    try {
      const signedUrl = await getSignedUrl(request.fileName);
      const response = RPCResponses.GetSignedUrl.create(signedUrl);
      sendResponse(response);
    } catch (error) {
      log.error(error);
    }
  };

  clearData = () => this.activityLogUploader.clearData();
  clearCache = () => caches.delete(CACHE_NAME);

  updateCurrentMonitoring = (change) => {
    if (this.monitoring == null) return;
    this.monitoring = { ...this.monitoring, ...change };
  };

  onAudioData = (data) => {
    if (
      this.monitoring &&
      this.inProgressEvent.eventType === "AWAKE" &&
      !this.isFreeVersion()
    ) {
      this.activityLogUploader.onData({
        data,
        fileName: this.inProgressEvent.fileName
      });
    }
  };

  monitoringEnded = async () => {
    if (this.monitoring == null) return;

    this.mediaRecorder?.destroy();
    this.mediaRecorder = null;
    this.pushNotificator?.destroy();
    this.pushNotificator = null;
    this.activityEmitter?.destroy();
    this.activityEmitter = null;
    this.getParents = null;
    this.updateParent = null;

    this.rpc.off(
      RPCRequests.StartActivityLog.name,
      this.handleStartActivityLogRpcRequest
    );
    this.rpc.off(
      RPCRequests.RefreshActivityLog.name,
      this.handleRefreshActivityLogRpcRequest
    );
    this.rpc.off(
      RPCRequests.StopActivityLog.name,
      this.handleStopActivityLogRpcRequest
    );
    this.rpc.off(
      RPCRequests.GetSignedUrl.name,
      this.handleGetSignedUrlRpcRequest
    );

    this.rpc = null;

    this.activityLogUploader.uploadFinishedMonitoring({
      monitoring: this.monitoring,
      lastEvent: this.createFinishedEvent(),
      isFreeVersion: this.isFreeVersion()
    });
  };
}
